import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import { useSession } from './session-context';
import { centralLoginUrl, surfaceOrigin } from './surface-links';
import './auth-gate.css';

// Single sign-on gate. There is no login form on this surface — sign-in lives on the
// one central login page (the website). On load the api-client tries GET /me, which
// 401s, triggers a cookie-backed /auth/refresh, and retries; if a valid shared
// session cookie exists the admin comes up signed in. If not, we bounce to the
// central login ONCE (carrying this URL as `next` so login returns here). A one-shot
// sessionStorage guard turns a would-be redirect loop — login succeeds but the
// cookie still isn't seen — into a visible error instead of an infinite bounce.
const BOUNCE_KEY = 'bd_login_bounce';

function readBounced(): boolean {
  try {
    return sessionStorage.getItem(BOUNCE_KEY) === '1';
  } catch {
    return false;
  }
}
function setBounced(v: boolean): void {
  try {
    if (v) sessionStorage.setItem(BOUNCE_KEY, '1');
    else sessionStorage.removeItem(BOUNCE_KEY);
  } catch {
    /* sessionStorage unavailable — the guard just degrades to always-bounce */
  }
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  const { user, loading, noMerchantRole } = useSession();
  const [looped, setLooped] = useState(false);

  useEffect(() => {
    if (user) {
      setBounced(false); // signed in — reset the guard for next time
      return;
    }
    if (loading) return;
    if (readBounced()) {
      setLooped(true); // came back from login still signed out — stop, show error
      return;
    }
    setBounced(true);
    window.location.href = centralLoginUrl(window.location.href);
  }, [user, loading]);

  // Signed in as a customer with no business yet: say so and offer the two ways on,
  // instead of a dashboard that cannot load (every merchant read is 403, API-219).
  if (user && noMerchantRole) {
    const website = surfaceOrigin('website') ?? '';
    const app = surfaceOrigin('app');
    return (
      <div className="auth-splash">
        <div className="auth-splash__box">
          <h1 className="auth-splash__title">{t('merchant.gate.no_biz_title')}</h1>
          <p className="auth-splash__sub">{t('merchant.gate.no_biz_sub')}</p>
          <a className="auth-splash__btn" href={`${website}/merchant-signup`}>
            {t('nav.add_business')}
          </a>
          {app && (
            <a className="auth-splash__link" href={`${app}/`}>
              {t('merchant.gate.to_customer')}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (user) return <>{children}</>;

  if (looped) {
    return (
      <div className="auth-splash">
        <div className="auth-splash__box">
          <h1 className="auth-splash__title">{t('auth.gate_error_title')}</h1>
          <p className="auth-splash__sub">{t('auth.gate_error_sub')}</p>
          <a
            className="auth-splash__btn"
            href={centralLoginUrl(window.location.href)}
            onClick={() => setBounced(false)}
          >
            {t('auth.go_to_login')}
          </a>
        </div>
      </div>
    );
  }

  return <div className="auth-splash">{t(loading ? 'common.loading' : 'auth.redirecting')}</div>;
}
